/**
 * AdminGuidePage.jsx
 * ==================
 *
 * Version web du Guide Administrateur, réservée au personnel interne.
 * - Route : /docs/admin
 * - Auth requis (staff interne uniquement, gate côté backend via is_internal_staff)
 * - Source : fragment HTML extrait du DOCX via mammoth
 * - Captures écran intégrées
 * - **CTA Hero** : choix entre Web Player onboarding admin et téléchargement PDF/PPTX
 * - Layout 3 colonnes Akamai-style via <GuideRenderer />
 */
import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Shield, PlayCircle, Download, FileText, ArrowRight } from 'lucide-react';
import GuideRenderer from '../../components/docs/GuideRenderer';
import { PermissionDenied } from '../../components/permissions/RequirePermission';
import { usePermissions } from '../../contexts/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOnboardingHero = () => (
  <section
    data-testid="admin-onboarding-hero"
    className="bg-gradient-to-br from-[#064E3B] via-[#047857] to-[#064E3B] text-white"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#FCD34D] mb-3">
            <Shield className="w-4 h-4" />
            <span>Espace Administrateur · Onboarding interactif</span>
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold leading-tight mb-3"
            style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
          >
            Pilotez Baobab Connect en <span className="text-[#FCD34D]">15 slides</span>.
          </h2>
          <p className="text-[#A7F3D0] text-base md:text-lg max-w-2xl leading-relaxed">
            Cockpit CFO, Audit Factures, RBAC, Opérations, Négociations, Messagerie, Rapports — tout ce qu'un admin doit
            maîtriser, en ≈ 10 minutes.
          </p>
        </div>
        <div className="flex flex-col gap-3 min-w-[260px]">
          <Link
            to="/docs/admin/onboarding"
            data-testid="admin-onboarding-launch-web-btn"
            className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-[#FCD34D] text-[#064E3B] rounded-xl font-bold text-base shadow-lg hover:bg-[#FBBF24] transition-all"
          >
            <span className="flex items-center gap-3">
              <PlayCircle className="w-6 h-6" />
              <span>Lancer le web player</span>
            </span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={`${BACKEND_URL}/api/uploads/docs/Formation_Admin_Baobab_Connect.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-onboarding-download-pdf-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-semibold text-white transition-colors"
            >
              <Download className="w-4 h-4" />
              PDF
            </a>
            <a
              href={`${BACKEND_URL}/api/uploads/docs/Formation_Admin_Baobab_Connect.pptx`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-onboarding-download-pptx-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-semibold text-white transition-colors"
            >
              <FileText className="w-4 h-4" />
              PPTX
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AdminGuideContent = () => {
  const axiosClient = axios;
  return (
    <>
      <AdminOnboardingHero />
      <GuideRenderer
        title="Guide Administrateur — Baobab Connect"
        accentColor="emerald"
        IconBrand={Shield}
        breadcrumbs={[
          { label: 'Administration', href: '/admin' },
          { label: 'Documentation' },
        ]}
        htmlEndpoint={`${BACKEND_URL}/api/docs/admin/html`}
        metaEndpoint={`${BACKEND_URL}/api/docs/admin/meta`}
        downloadDocxUrl={`${BACKEND_URL}/api/uploads/docs/Guide_Admin_Baobab_Connect.docx`}
        backUrl="/admin"
        backLabel="Retour à l'administration"
        axiosClient={axiosClient}
        testIdPrefix="admin-guide"
      />
    </>
  );
};

const AdminGuidePage = () => {
  const { isAdmin, hasAnyPermission } = usePermissions();
  const isStaff =
    isAdmin ||
    hasAnyPermission([
      'operations.view.all',
      'finances.view.all',
      'reports.view_dashboard',
    ]);
  if (!isStaff) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center" data-testid="admin-guide-page">
        <div className="max-w-lg w-full">
          <PermissionDenied
            title="Guide réservé au personnel interne"
            hint="Si vous êtes un client, consultez le Guide Client public via /docs/client."
          />
        </div>
      </div>
    );
  }
  return (
    <div data-testid="admin-guide-page">
      <AdminGuideContent />
    </div>
  );
};

export default AdminGuidePage;
